import React from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';
import { propTypes as formProps, reduxForm } from 'redux-form';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { setAutomationElement } from '@experian-uk/corvetteuk-common-ui';
import Analytics, { InteractionInputEvent } from '@experian-uk/web-common-analytics';

import {
  ActionsContainer,
  Content,
  Heading,
  FlexibleContainer,
  SpinnerContainer,
  StrongStyledCopy,
  StyledCopy,
  SubmitButton,
  TakeCareContainer,
  VoucherInput,
} from './index.styles';
import { textInput } from '../../../helpers/formStyledComponent';
import { normalize } from '../../../helpers/forms';
import validateVoucher from '../../../actions/voucherValidation';
import { Spinner } from '../../../components/Forms/index.styles';
import getEnv from '../../../components/Context/env/getEnv';
import { idaas, errorMessages } from '../../../constants';
import checkClientValidVoucher from '../../../helpers/checkClientValidVoucher';

const env = getEnv();

const customErrors = {
  voucherCode: {
    required: 'Please enter your voucher code',
  },
};

const inputProps = {
  maxLength: 255,
  placeholder: 'ENTER ACTIVATION CODE',
  stretchErrorFrom: '15',
};

const schema = {
  voucherCode: Joi.string().required(),
};

const getEcd2Naming = errorMessage => {
  switch (errorMessage) {
    case errorMessages.voucherValidation.invalidVoucherCode:
      return 'Voucher Not Recognised';

    case errorMessages.voucherValidation.expiredVoucherCode:
      return 'Voucher Expired';

    case errorMessages.voucherValidation.usedVoucherCode:
      return 'Voucher Already Used';

    case errorMessages.voucherValidation.technicalError:
      return 'Voucher Service Technical Issue';

    default:
      return 'unknown error';
  }
};

const pushVoucherErrorECD2Event = label => {
  Analytics.publishOnce(
    InteractionInputEvent.fromObject({
      component: 'id_voucher_input_validation',
      action: 'click',
      status: 'failure',
      data: {
        errorType: label,
      },
    })
  );
};

@connect(
  state => ({
    apiError: state.voucherValidationResult.error,
    fetching: state.voucherValidationResult.fetching,
    validVoucher: state.voucherValidationResult.voucherCode,
  }),
  dispatch => bindActionCreators({ validateVoucher }, dispatch)
)
@reduxForm({
  form: 'billing/voucher-code',
})
export default class TakeCarePanel extends React.Component {
  static propTypes = {
    ...formProps,
    apiError: PropTypes.string,
    clientSideError: PropTypes.bool,
    fetching: PropTypes.bool,
    voucher: PropTypes.string,
    validateVoucher: PropTypes.func.isRequired,
  };

  static defaultProps = {
    apiError: null,
    clientSideError: null,
    voucher: '',
    fetching: false,
  };

  state = {
    shouldShowApiError: false,
    shouldShowClientSideError: false,
    voucherErrorMessage: '',
  };

  componentDidMount() {
    const { voucher } = this.props;

    if (voucher) {
      this.props.initialize({
        voucherCode: encodeURI(this.props.voucher.toUpperCase()),
      });
    }
  }

  fieldChanged = () => {
    this.setState({
      shouldShowApiError: false,
      shouldShowClientSideError: false,
      voucherErrorMessage: '',
    });
  };

  submit = async values => {
    this.setState({
      shouldShowApiError: false,
      shouldShowClientSideError: false,
    });
    const { voucherCode } = values;

    const clientValidationCheck = checkClientValidVoucher(voucherCode, env.REACT_APP_ENV);

    if (clientValidationCheck.shouldShowClientSideError) {
      this.setState({
        shouldShowClientSideError: true,
        voucherErrorMessage: clientValidationCheck.voucherErrorMessage,
      });
      pushVoucherErrorECD2Event(clientValidationCheck.ecd2EventLabel);
      return;
    }

    await this.props.validateVoucher(voucherCode);
    if (this.props.apiError) {
      this.setState({
        shouldShowApiError: true,
        shouldShowClientSideError: false,
      });
      pushVoucherErrorECD2Event(getEcd2Naming(this.props.apiError));
    } else {
      const signupUrl = `${env.REACT_APP_C2_URL}/api/sso/signin?issuerType=${idaas.issuerType}&register=true&loa=verified&relayState=brand%3Dcreditmatcher%3BofferId%3D${idaas.offerId}%3BvoucherCode%3D${voucherCode}`;
      window.location = signupUrl;
    }
  };

  render() {
    return (
      <form method="post" onSubmit={this.props.handleSubmit(this.submit)}>
        <TakeCareContainer {...setAutomationElement('takeCare')}>
          <FlexibleContainer>
            <Content>
              <Heading>
                Protect yourself with Identity <span>Plus</span>
              </Heading>
              <StyledCopy>
                Welcome to your complimentary Identity Plus Subscription. We&rsquo;ll monitor your personal, financial
                and credit information around the clock. If we see any suspicious activity, you&rsquo;ll be the first to
                know. Think you&rsquo;ve been a victim of fraud? Don&rsquo;t worry. Our expert advisers are on hand to
                help sort things out.
              </StyledCopy>
              <StrongStyledCopy>
                Help shut the door on fraudsters. Protect yourself with Identity Plus.
              </StrongStyledCopy>
              <ActionsContainer>
                <VoucherInput
                  automation="voucher-code-entry"
                  applyError={
                    this.state.shouldShowClientSideError || this.state.shouldShowApiError
                      ? this.state.voucherErrorMessage || this.props.apiError
                      : null
                  }
                  component={textInput}
                  disabled={this.props.fetching || this.props.validVoucher}
                  errorMessages={customErrors}
                  inputProps={inputProps}
                  name="voucherCode"
                  normalize={normalize.uppercase}
                  onChange={this.fieldChanged}
                  schema={schema}
                />
                {this.props.fetching || this.props.validVoucher ? (
                  <SpinnerContainer>
                    <Spinner />
                  </SpinnerContainer>
                ) : (
                  <SubmitButton
                    isButton
                    label="Get started"
                    type="upgrade"
                    {...setAutomationElement('submit-voucher-code')}
                  />
                )}
              </ActionsContainer>
            </Content>
          </FlexibleContainer>
        </TakeCareContainer>
      </form>
    );
  }
}
